import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  confirmIndustrialCostLaborProductRatesImport,
  createIndustrialCostLaborProductRate,
  createIndustrialCostLaborStageCost,
  deleteIndustrialCostLaborProductRate,
  deleteIndustrialCostLaborStageCost,
  getIndustrialCostLaborModuleConfig,
  getIndustrialCostLaborProductRates,
  getIndustrialCostLaborResults,
  getIndustrialCostLaborStageCosts,
  getIndustrialCostProducts,
  getIndustrialCostStages,
  previewIndustrialCostLaborProductRatesImport,
  updateIndustrialCostLaborModuleConfig,
  updateIndustrialCostLaborProductRate,
  updateIndustrialCostLaborStageCost,
} from '../services/industrialCostService';
import type {
  LaborAllocationModel,
  LaborProductRate,
  LaborProductRateImportPreviewResponse,
  LaborProductionBasis,
  LaborResultSummary,
  LaborStageCost,
  LaborValuationMode,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
const MODEL_OPTIONS = [
  { value: 'stage_based', label: 'Por etapa' },
  { value: 'product_rate_based', label: 'Por producto' },
  { value: 'mixed', label: 'Mixto' },
] as const;

const VALUATION_OPTIONS = [
  { value: 'monthly_total', label: 'Coste total' },
  { value: 'hourly_rate', label: 'Coste hora' },
] as const;

const TAB_OPTIONS = [
  { value: 'stage', label: 'Imputación por etapa' },
  { value: 'product', label: 'Imputación por producto' },
] as const;

const BASIS_OPTIONS = [
  { value: 'units', label: 'Unidades' },
  { value: 'kg', label: 'Kg' },
  { value: 'meters', label: 'Metros' },
] as const;

type LaborTab = (typeof TAB_OPTIONS)[number]['value'];
type ProductImportStep = 'fields' | 'values' | 'summary';

type StageCostFormState = {
  stage_id: string;
  valuation_mode: LaborValuationMode;
  monthly_total_amount: string;
  labor_hour_cost: string;
  hours_total: string;
  notes: string;
  is_active: boolean;
};

type ProductRateFormState = {
  product_id: string;
  stage_id: string;
  production_basis: LaborProductionBasis;
  units_per_hour: string;
  valuation_mode: LaborValuationMode;
  labor_hour_cost: string;
  notes: string;
  is_active: boolean;
};

const EMPTY_STAGE_COST_FORM: StageCostFormState = {
  stage_id: '',
  valuation_mode: 'monthly_total',
  monthly_total_amount: '',
  labor_hour_cost: '',
  hours_total: '',
  notes: '',
  is_active: true,
};

const EMPTY_PRODUCT_RATE_FORM: ProductRateFormState = {
  product_id: '',
  stage_id: '',
  production_basis: 'units',
  units_per_hour: '',
  valuation_mode: 'hourly_rate',
  labor_hour_cost: '',
  notes: '',
  is_active: true,
};

const MONTH_OPTIONS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string | { message?: string } } };
      message?: string;
    };

    const detail = maybeResponse.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string') {
      return detail.message;
    }
    if (maybeResponse.message) return maybeResponse.message;
  }

  return 'Se produjo un error inesperado.';
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';

  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';

  return esNumberFormat({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function parseNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
}

function buildCalculationExplanation(model: LaborAllocationModel, valuationMode: LaborValuationMode): string {
  if (model === 'stage_based' && valuationMode === 'monthly_total') {
    return 'PROA tomará el coste laboral mensual informado para cada etapa y lo repartirá a producto con la lógica de etapa ya configurada.';
  }

  if (model === 'stage_based' && valuationMode === 'hourly_rate') {
    return 'PROA valorará cada etapa con coste hora y horas del periodo para obtener el coste laboral total imputable de esa etapa.';
  }

  if (model === 'product_rate_based' && valuationMode === 'monthly_total') {
    return 'PROA convertirá la producción real en horas consumidas según el rendimiento de cada producto y usará ese tiempo para repartir el coste mensual de etapa.';
  }

  if (model === 'product_rate_based' && valuationMode === 'hourly_rate') {
    return 'PROA convertirá la producción real en horas consumidas según el rendimiento del producto y las valorará con el coste hora configurado.';
  }

  if (model === 'mixed' && valuationMode === 'monthly_total') {
    return 'PROA combinará una capa laboral por etapa y otra por producto, partiendo de importes mensuales informados.';
  }

  return 'PROA combinará una capa laboral por etapa y otra por producto, valorando la imputación con coste hora.';
}

function formatCoverageStatus(value: string | null | undefined): string {
  if (value === 'configured') return 'Configurado';
  if (value === 'partial') return 'Parcial';
  if (value === 'pending') return 'Pendiente';
  return value || '—';
}

function formatBasisLabel(value: string | null | undefined): string {
  if (value === 'units') return 'Unidades';
  if (value === 'kg') return 'Kg';
  if (value === 'meters') return 'Metros';
  return value || '—';
}

export default function IndustrialCostLaborView() {
  const currentDate = new Date();

  const [selectedYear, setSelectedYear] = useState(String(currentDate.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(currentDate.getMonth() + 1));
  const [laborModel, setLaborModel] = useState<LaborAllocationModel>('stage_based');
  const [valuationMode, setValuationMode] = useState<LaborValuationMode>('monthly_total');
  const [activeTab, setActiveTab] = useState<LaborTab>('stage');

  const [stages, setStages] = useState<Array<Record<string, any>>>([]);
  const [products, setProducts] = useState<Array<Record<string, any>>>([]);
  const [stageCosts, setStageCosts] = useState<LaborStageCost[]>([]);
  const [productRates, setProductRates] = useState<LaborProductRate[]>([]);
  const [results, setResults] = useState<LaborResultSummary | null>(null);
  const [savedConfig, setSavedConfig] = useState<{
    model: LaborAllocationModel;
    valuation_mode: LaborValuationMode;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingStageCost, setSavingStageCost] = useState(false);
  const [savingProductRate, setSavingProductRate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingStageCost, setEditingStageCost] = useState<LaborStageCost | null>(null);
  const [stageCostForm, setStageCostForm] = useState<StageCostFormState>(EMPTY_STAGE_COST_FORM);

  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProductRate, setEditingProductRate] = useState<LaborProductRate | null>(null);
  const [productRateForm, setProductRateForm] = useState<ProductRateFormState>(EMPTY_PRODUCT_RATE_FORM);

  const [productImportDialogOpen, setProductImportDialogOpen] = useState(false);
  const [productImportStep, setProductImportStep] = useState<ProductImportStep>('fields');
  const [productImportFile, setProductImportFile] = useState<File | null>(null);
  const [productImportPreview, setProductImportPreview] = useState<LaborProductRateImportPreviewResponse | null>(null);
  const [productImportMapping, setProductImportMapping] = useState<Record<string, string | null>>({});
  const [productImportProductResolution, setProductImportProductResolution] = useState<Record<string, string | null>>({});
  const [productImportStageResolution, setProductImportStageResolution] = useState<Record<string, string | null>>({});
  const [previewingProductImport, setPreviewingProductImport] = useState(false);
  const [confirmingProductImport, setConfirmingProductImport] = useState(false);

  const explanation = useMemo(
    () => buildCalculationExplanation(laborModel, valuationMode),
    [laborModel, valuationMode],
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear, currentYear + 1].map(String);
  }, []);

  async function loadReferenceData() {
    const [stagesData, productsData] = await Promise.all([
      getIndustrialCostStages(),
      getIndustrialCostProducts(),
    ]);

    setStages(stagesData);
    setProducts(productsData);
  }

  async function loadConfig() {
    const config = await getIndustrialCostLaborModuleConfig();
    setLaborModel(config.model);
    setValuationMode(config.valuation_mode);
    setSavedConfig({
      model: config.model,
      valuation_mode: config.valuation_mode,
    });

    if (config.model === 'stage_based') setActiveTab('stage');
    if (config.model === 'product_rate_based') setActiveTab('product');
  }

  async function loadStageCosts() {
    const data = await getIndustrialCostLaborStageCosts({
      period_year: Number(selectedYear),
      period_month: Number(selectedMonth),
    });
    setStageCosts(data);
  }

  async function loadProductRates() {
    const data = await getIndustrialCostLaborProductRates();
    setProductRates(data);
  }

  async function loadResults() {
    const data = await getIndustrialCostLaborResults({
      period_year: Number(selectedYear),
      period_month: Number(selectedMonth),
    });
    setResults(data);
  }

  async function loadAll() {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        loadReferenceData(),
        loadConfig(),
        loadStageCosts(),
        loadProductRates(),
        loadResults(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;

    void (async () => {
      try {
        setError(null);
        await Promise.all([loadStageCosts(), loadResults()]);
      } catch (err) {
        setError(getErrorMessage(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  function openCreateStageCostDialog() {
    setEditingStageCost(null);
    setStageCostForm({
      ...EMPTY_STAGE_COST_FORM,
      valuation_mode: valuationMode,
    });
    setStageDialogOpen(true);
  }

  function openEditStageCostDialog(row: LaborStageCost) {
    setEditingStageCost(row);
    setStageCostForm({
      stage_id: row.stage_id,
      valuation_mode: row.valuation_mode,
      monthly_total_amount: row.monthly_total_amount != null ? String(row.monthly_total_amount) : '',
      labor_hour_cost: row.labor_hour_cost != null ? String(row.labor_hour_cost) : '',
      hours_total: row.hours_total != null ? String(row.hours_total) : '',
      notes: row.notes || '',
      is_active: row.is_active,
    });
    setStageDialogOpen(true);
  }

  function closeStageDialog() {
    setStageDialogOpen(false);
    setEditingStageCost(null);
    setStageCostForm(EMPTY_STAGE_COST_FORM);
  }

  function openCreateProductRateDialog() {
    setEditingProductRate(null);
    setProductRateForm({
      ...EMPTY_PRODUCT_RATE_FORM,
      valuation_mode: valuationMode === 'monthly_total' ? 'hourly_rate' : valuationMode,
    });
    setProductDialogOpen(true);
  }

  function openEditProductRateDialog(row: LaborProductRate) {
    setEditingProductRate(row);
    setProductRateForm({
      product_id: row.product_id,
      stage_id: row.stage_id || '',
      production_basis: row.production_basis,
      units_per_hour: String(row.units_per_hour),
      valuation_mode: row.valuation_mode,
      labor_hour_cost: row.labor_hour_cost != null ? String(row.labor_hour_cost) : '',
      notes: row.notes || '',
      is_active: row.is_active,
    });
    setProductDialogOpen(true);
  }

  function closeProductDialog() {
    setProductDialogOpen(false);
    setEditingProductRate(null);
    setProductRateForm(EMPTY_PRODUCT_RATE_FORM);
  }

  function openProductImportDialog() {
    setProductImportDialogOpen(true);
    setProductImportStep('fields');
    setProductImportFile(null);
    setProductImportPreview(null);
    setProductImportMapping({});
    setProductImportProductResolution({});
    setProductImportStageResolution({});
  }

  function closeProductImportDialog() {
    setProductImportDialogOpen(false);
    setProductImportStep('fields');
    setProductImportFile(null);
    setProductImportPreview(null);
    setProductImportMapping({});
    setProductImportProductResolution({});
    setProductImportStageResolution({});
  }

  function goToProductImportFieldsStep() {
    setProductImportStep('fields');
  }

  function goToProductImportValuesStep() {
    setProductImportStep('values');
  }

  async function handleGoToProductImportSummaryStep() {
    if (!productImportFile) return;

    await handlePreviewProductImport(
      productImportFile,
      productImportMapping,
      productImportProductResolution,
      productImportStageResolution,
    );

    setProductImportStep('summary');
  }

  async function handlePreviewProductImport(
    fileToPreview: File,
    mappingOverride?: Record<string, string | null>,
    productResolutionOverride?: Record<string, string | null>,
    stageResolutionOverride?: Record<string, string | null>,
  ) {
    setPreviewingProductImport(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const effectiveProductResolution = productResolutionOverride ?? productImportProductResolution;
      const effectiveStageResolution = stageResolutionOverride ?? productImportStageResolution;

      const preview = await previewIndustrialCostLaborProductRatesImport(
        fileToPreview,
        mappingOverride,
        effectiveProductResolution,
        effectiveStageResolution,
      );

      setProductImportFile(fileToPreview);
      setProductImportPreview(preview);
      setProductImportMapping(preview.auto_mapping);
      setProductImportProductResolution(effectiveProductResolution);
      setProductImportStageResolution(effectiveStageResolution);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPreviewingProductImport(false);
    }
  }

  async function handleConfirmProductImport() {
    if (!productImportFile) return;

    setConfirmingProductImport(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await confirmIndustrialCostLaborProductRatesImport(
        productImportFile,
        productImportMapping,
        productImportProductResolution,
        productImportStageResolution,
      );

      closeProductImportDialog();
      setSuccessMessage(
        `Importación de mano de obra completada. Filas importadas: ${response.rows_imported}. Completas: ${response.rows_imported_complete}. Parciales: ${response.rows_imported_partial}. Creadas: ${response.rows_created}. Actualizadas: ${response.rows_updated}.`,
      );
      await Promise.all([loadProductRates(), loadResults()]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setConfirmingProductImport(false);
    }
  }

  async function handleSaveConfig() {
    setError(null);
    setSuccessMessage(null);

    if (laborModel === 'mixed') {
      const confirmedMixed = window.confirm(
        'El modelo mixto todavía no está implementado en cálculo. Puedes guardarlo como configuración, pero no se aplicará correctamente en resultados. ¿Quieres continuar?',
      );
      if (!confirmedMixed) return;
    }

    if (savedConfig && savedConfig.model !== laborModel) {
      let confirmationMessage =
        'Vas a cambiar el modelo activo de imputación. Los datos ya cargados no se eliminarán, pero la capa no activa dejará de participar en el cálculo.';

      if (laborModel === 'stage_based' && productRecordsCount > 0) {
        confirmationMessage =
          `Vas a cambiar a imputación por etapa. Hay ${productRecordsCount} rendimiento(s) por producto cargado(s) que dejarán de participar en el cálculo, aunque seguirán guardados. ¿Quieres continuar?`;
      } else if (laborModel === 'product_rate_based' && stageRecordsCount > 0) {
        confirmationMessage =
          `Vas a cambiar a imputación por producto. Hay ${stageRecordsCount} coste(s) por etapa cargado(s) que dejarán de participar en el cálculo, aunque seguirán guardados. ¿Quieres continuar?`;
      } else if (laborModel === 'mixed') {
        confirmationMessage =
          'Vas a cambiar al modelo mixto. La configuración se guardará, pero el cálculo mixto todavía no está implementado. ¿Quieres continuar?';
      }

      const confirmed = window.confirm(confirmationMessage);
      if (!confirmed) return;
    }

    setSavingConfig(true);

    try {
      const saved = await updateIndustrialCostLaborModuleConfig({
        model: laborModel,
        valuation_mode: valuationMode,
      });

      setLaborModel(saved.model);
      setValuationMode(saved.valuation_mode);
      setSavedConfig({
        model: saved.model,
        valuation_mode: saved.valuation_mode,
      });

      if (saved.model === 'stage_based') setActiveTab('stage');
      if (saved.model === 'product_rate_based') setActiveTab('product');

      await loadResults();
      setSuccessMessage('Configuración de mano de obra guardada correctamente.');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleSubmitStageCost() {
    setSavingStageCost(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        period_year: Number(selectedYear),
        period_month: Number(selectedMonth),
        stage_id: stageCostForm.stage_id,
        valuation_mode: stageCostForm.valuation_mode,
        monthly_total_amount:
          stageCostForm.valuation_mode === 'monthly_total'
            ? parseNullableNumber(stageCostForm.monthly_total_amount)
            : null,
        labor_hour_cost:
          stageCostForm.valuation_mode === 'hourly_rate'
            ? parseNullableNumber(stageCostForm.labor_hour_cost)
            : null,
        hours_total:
          stageCostForm.valuation_mode === 'hourly_rate'
            ? parseNullableNumber(stageCostForm.hours_total)
            : null,
        notes: stageCostForm.notes.trim() || null,
        is_active: stageCostForm.is_active,
      };

      if (editingStageCost) {
        await updateIndustrialCostLaborStageCost(editingStageCost.id, payload);
        setSuccessMessage('Coste laboral por etapa actualizado correctamente.');
      } else {
        await createIndustrialCostLaborStageCost(payload);
        setSuccessMessage('Coste laboral por etapa creado correctamente.');
      }

      closeStageDialog();
      await Promise.all([loadStageCosts(), loadResults()]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingStageCost(false);
    }
  }

  async function handleDeleteStageCost(row: LaborStageCost) {
    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar el coste laboral de la etapa ${row.stage_name || row.stage_code || row.stage_id}?`,
    );
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostLaborStageCost(row.id);
      setSuccessMessage('Coste laboral por etapa eliminado correctamente.');
      await Promise.all([loadStageCosts(), loadResults()]);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  async function handleSubmitProductRate() {
    setSavingProductRate(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        product_id: productRateForm.product_id,
        stage_id: productRateForm.stage_id || null,
        production_basis: productRateForm.production_basis,
        units_per_hour: Number(productRateForm.units_per_hour),
        valuation_mode: productRateForm.valuation_mode,
        labor_hour_cost: parseNullableNumber(productRateForm.labor_hour_cost),
        notes: productRateForm.notes.trim() || null,
        is_active: productRateForm.is_active,
      };

      if (editingProductRate) {
        await updateIndustrialCostLaborProductRate(editingProductRate.id, payload);
        setSuccessMessage('Rendimiento laboral por producto actualizado correctamente.');
      } else {
        await createIndustrialCostLaborProductRate(payload);
        setSuccessMessage('Rendimiento laboral por producto creado correctamente.');
      }

      closeProductDialog();
      await Promise.all([loadProductRates(), loadResults()]);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingProductRate(false);
    }
  }

  async function handleDeleteProductRate(row: LaborProductRate) {
    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar el rendimiento laboral de ${row.product_name || row.product_code || row.product_id}?`,
    );
    if (!confirmed) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostLaborProductRate(row.id);
      setSuccessMessage('Rendimiento laboral por producto eliminado correctamente.');
      await Promise.all([loadProductRates(), loadResults()]);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }

  const stageTabDisabled = laborModel === 'product_rate_based';
  const productTabDisabled = laborModel === 'stage_based';

  const stageRecordsCount = stageCosts.length;
  const productRecordsCount = productRates.length;

  const activeLayerLabel =
    laborModel === 'stage_based'
      ? 'Imputación por etapa'
      : laborModel === 'product_rate_based'
        ? 'Imputación por producto'
        : 'Mixto';
        
  const hasUnsavedConfigChanges =
    savedConfig !== null &&
    (savedConfig.model !== laborModel || savedConfig.valuation_mode !== valuationMode);

  const stageLayerStatus =
    stageRecordsCount > 0
      ? laborModel === 'stage_based'
        ? 'Activa'
        : 'Guardada'
      : 'Sin datos';

  const productLayerStatus =
    productRecordsCount > 0
      ? laborModel === 'product_rate_based'
        ? 'Activa'
        : 'Guardada'
      : 'Sin datos';

  const pendingProductsCount =
    results?.rows.filter((row) => row.coverage_status !== 'configured').length ?? 0;

  const unsupportedMetersCount =
    results?.warnings.filter((warning) => warning.type === 'unsupported_production_basis').length ?? 0;

  const compactSummary = `${activeLayerLabel} · Etapa: ${stageLayerStatus} · Producto: ${productLayerStatus}`;

  const stageDialogCanSubmit =
    stageCostForm.stage_id &&
    (
      (stageCostForm.valuation_mode === 'monthly_total' && parseNullableNumber(stageCostForm.monthly_total_amount) !== null) ||
      (
        stageCostForm.valuation_mode === 'hourly_rate' &&
        parseNullableNumber(stageCostForm.labor_hour_cost) !== null &&
        parseNullableNumber(stageCostForm.hours_total) !== null
      )
    );

  const productDialogCanSubmit =
    productRateForm.product_id &&
    productRateForm.stage_id &&
    Number(productRateForm.units_per_hour) > 0;

  const unresolvedProductRefs = productImportPreview?.detected_product_refs.filter((item) => !item.is_resolved) ?? [];
  const unresolvedStageRefs = productImportPreview?.detected_stage_refs.filter((item) => !item.is_resolved) ?? [];

  const productImportCanGoToValues =
    !!productImportPreview &&
    productImportPreview.missing_required_fields.length === 0;

  const productImportCanGoToSummary =
    !!productImportPreview;

  const productImportCanConfirm =
    !!productImportFile &&
    !!productImportPreview &&
    productImportPreview.missing_required_fields.length === 0 &&
    productImportPreview.coverage_summary.rows_importable > 0;

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Mano de obra
            </Typography>
            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Configura cómo se imputa la mano de obra del periodo y visualiza el resultado del modelo activo.
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              Esta vista unifica selección del modelo, carga de datos y lectura del resultado para mantener el criterio completo en una sola pantalla.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              select
              size="small"
              label="Año"
              value={selectedYear}
              onChange={(event) => setSelectedYear(event.target.value)}
              sx={{ minWidth: 110 }}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Mes"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              sx={{ minWidth: 130 }}
            >
              {MONTH_OPTIONS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.25 },
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Configuración de imputación
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.35 }}>
                Define el modelo activo y la base económica que utilizará PROA.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => void handleSaveConfig()}
              disabled={savingConfig || !hasUnsavedConfigChanges}
            >
              Guardar configuración
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            <TextField
              select
              label="Modelo de imputación"
              value={laborModel}
              onChange={(event) => {
                const nextModel = event.target.value as LaborAllocationModel;
                setLaborModel(nextModel);
                if (nextModel === 'stage_based') setActiveTab('stage');
                if (nextModel === 'product_rate_based') setActiveTab('product');
              }}
              size="small"
              fullWidth
            >
              {MODEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Valoración económica"
              value={valuationMode}
              onChange={(event) => setValuationMode(event.target.value as LaborValuationMode)}
              size="small"
              fullWidth
            >
              {VALUATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(34,211,238,0.18)',
              bgcolor: 'rgba(8,47,73,0.20)',
              px: 1.75,
              py: 1.35,
            }}
          >
            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ color: '#bae6fd', fontWeight: 800 }}>
                Estado actual
              </Typography>
              <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                {compactSummary}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.5 }}>
                {explanation}
              </Typography>
            </Stack>
          </Paper>

          {pendingProductsCount > 0 ? (
            <Alert severity="warning">
              Hay {pendingProductsCount} producto(s) del periodo con cobertura laboral incompleta o pendiente.
            </Alert>
          ) : null}

          {unsupportedMetersCount > 0 ? (
            <Alert severity="warning">
              Existen referencias con base en metros que todavía no están resueltas correctamente en el cálculo actual.
            </Alert>
          ) : null}

          {laborModel === 'stage_based' && productRates.length > 0 ? (
            <Alert severity="info">
              Hay {productRates.length} rendimiento(s) por producto guardado(s), pero no participan en el cálculo mientras el modelo activo sea por etapa.
            </Alert>
          ) : null}

          {laborModel === 'product_rate_based' && stageCosts.length > 0 ? (
            <Alert severity="info">
              Hay {stageCosts.length} coste(s) por etapa guardado(s), pero no participan en el cálculo mientras el modelo activo sea por producto.
            </Alert>
          ) : null}

          {laborModel === 'mixed' ? (
            <Alert severity="warning">
              El modelo mixto sigue visible como configuración futura, pero todavía no está implementado completamente en el cálculo.
            </Alert>
          ) : null}

          {hasUnsavedConfigChanges ? (
            <Alert severity="info">
              Tienes cambios de configuración pendientes de guardar.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(148,163,184,0.14)',
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {TAB_OPTIONS.map((tab) => {
              const selected = activeTab === tab.value;
              const disabled = tab.value === 'stage' ? stageTabDisabled : productTabDisabled;

              return (
                <Button
                  key={tab.value}
                  variant={selected ? 'contained' : 'outlined'}
                  disabled={disabled}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </Button>
              );
            })}
          </Box>

          {activeTab === 'stage' ? (
            <Button variant="contained" onClick={openCreateStageCostDialog}>
              Nuevo coste por etapa
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon />}
                onClick={openProductImportDialog}
              >
                Importar archivo
              </Button>
              <Button variant="contained" onClick={openCreateProductRateDialog}>
                Nuevo rendimiento por producto
              </Button>
            </Stack>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Cargando mano de obra...
            </Typography>
          ) : activeTab === 'stage' ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Imputación por etapa
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Gestiona la base laboral por etapa del periodo seleccionado.
                </Typography>
              </Box>

              {pendingProductsCount > 0 ? (
                <Alert severity="warning">
                  Hay productos del periodo que no están absorbiendo mano de obra correctamente con la configuración actual de etapas.
                </Alert>
              ) : null}

              {stageCosts.length === 0 ? (
                <Alert severity="info">No hay costes laborales por etapa cargados para este periodo.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapa</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Valoración</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste total</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste hora</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Horas</TableCell>
                        <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>Coste resuelto</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stageCosts.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                {row.stage_name || row.stage_id}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {row.stage_code || 'Sin código'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>
                            {row.valuation_mode === 'monthly_total' ? 'Coste total' : 'Coste hora'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                            {formatCurrency(row.monthly_total_amount)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                            {formatCurrency(row.labor_hour_cost)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                            {formatNumber(row.hours_total)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                            {formatCurrency(row.resolved_cost_amount)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditStageCostDialog(row)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => void handleDeleteStageCost(row)}>
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Imputación por producto
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Gestiona el rendimiento de producto y su valoración para traducir producción real en horas consumidas.
                </Typography>
              </Box>

              {pendingProductsCount > 0 ? (
                <Alert severity="warning">
                  Hay referencias con producción en el periodo que todavía no tienen imputación laboral resuelta con el modelo activo.
                </Alert>
              ) : null}

              {productRates.length === 0 ? (
                <Alert severity="info">No hay rendimientos laborales por producto cargados.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapa</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Base</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Rendimiento</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Valoración</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste hora</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productRates.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                {row.product_name || row.product_id}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {row.product_code || 'Sin código'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>
                            {row.stage_name || row.stage_code || '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>
                            {formatBasisLabel(row.production_basis)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                            {formatNumber(row.units_per_hour, 4)}
                          </TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>
                            {row.valuation_mode === 'monthly_total' ? 'Coste total' : 'Coste hora'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                            {formatCurrency(row.labor_hour_cost)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditProductRateDialog(row)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => void handleDeleteProductRate(row)}>
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: 3,
          py: 3,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
              Resultados y cobertura
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Resultado vivo del modelo activo para el periodo seleccionado.
            </Typography>
          </Box>

          {!results ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Sin resultados disponibles todavía.
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                {[
                  { label: 'Coste laboral imputado', value: formatCurrency(results.total_labor_cost) },
                  { label: 'Cobertura', value: formatCoverageStatus(results.coverage_status) },
                  { label: 'Etapas configuradas', value: String(results.configured_stage_count) },
                  { label: 'Productos configurados', value: String(results.configured_product_count) },
                  { label: 'Pendientes', value: String(results.pending_stage_count + results.pending_product_count) },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.16)',
                      bgcolor: 'rgba(2,6,23,0.32)',
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontSize: '1.2rem', fontWeight: 900, mt: 0.75 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {results.warnings.length > 0 ? (
                <Stack spacing={1}>
                  {results.warnings
                    .filter(
                      (warning) =>
                        String((warning as Record<string, unknown>).type ?? '') ===
                        'multiple_configured_labor_product_rate_bases',
                    )
                    .map((warning, index) => {
                      const item = warning as Record<string, unknown>;
                      const productCode = String(item.product_code ?? item.product_id ?? 'Producto');
                      const productName = String(item.product_name ?? '');
                      const configuredBases = Array.isArray(item.configured_bases)
                        ? item.configured_bases.map((value) => String(value)).join(', ')
                        : '—';

                      return (
                        <Alert key={`labor-multi-config-${index}`} severity="info">
                          <strong>{productCode}</strong>
                          {productName ? ` · ${productName}` : ''} tiene varias bases configuradas: {configuredBases}. Revisa cuál debe permanecer activa para evitar ambigüedades en periodos futuros.
                        </Alert>
                      );
                    })}

                  {results.warnings
                    .filter(
                      (warning) =>
                        String((warning as Record<string, unknown>).type ?? '') ===
                        'ambiguous_labor_product_rate_basis',
                    )
                    .map((warning, index) => {
                      const item = warning as Record<string, unknown>;
                      const productCode = String(item.product_code ?? item.product_id ?? 'Producto');
                      const productName = String(item.product_name ?? '');
                      const applicableBases = Array.isArray(item.applicable_bases)
                        ? item.applicable_bases.map((value) => String(value)).join(', ')
                        : '—';

                      return (
                        <Alert key={`labor-ambiguity-${index}`} severity="warning">
                          <strong>{productCode}</strong>
                          {productName ? ` · ${productName}` : ''} tiene varias bases aplicables en el periodo: {applicableBases}. PROA no aplica mano de obra automática hasta dejar una única base válida para ese producto.
                        </Alert>
                      );
                    })}

                  <Alert severity="warning">
                    Hay incidencias en la imputación de mano de obra del periodo. Revisa la cobertura y los datos configurados.
                  </Alert>
                </Stack>
              ) : null}

              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Base</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Producción</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Rendimiento</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Horas</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste hora</TableCell>
                      <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>Coste laboral</TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Cobertura</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.rows.map((row) => (
                      <TableRow key={row.product_id} hover>
                        <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                              {row.product_name || row.product_id}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {row.product_code || 'Sin código'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {formatBasisLabel(row.production_basis)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                          {formatNumber(row.produced_amount)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                          {formatNumber(row.units_per_hour, 4)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                          {formatNumber(row.hours_consumed, 4)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                          {formatCurrency(row.labor_hour_cost)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {formatCurrency(row.labor_cost_amount)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {formatCoverageStatus(row.coverage_status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      <Dialog open={stageDialogOpen} onClose={closeStageDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingStageCost ? 'Editar coste por etapa' : 'Nuevo coste por etapa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Etapa"
              value={stageCostForm.stage_id}
              onChange={(event) => setStageCostForm((prev) => ({ ...prev, stage_id: event.target.value }))}
              fullWidth
              size="small"
            >
              {stages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {[stage.code, stage.name].filter(Boolean).join(' · ')}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Valoración"
              value={stageCostForm.valuation_mode}
              onChange={(event) =>
                setStageCostForm((prev) => ({
                  ...prev,
                  valuation_mode: event.target.value as LaborValuationMode,
                }))
              }
              fullWidth
              size="small"
            >
              {VALUATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {stageCostForm.valuation_mode === 'monthly_total' ? (
              <TextField
                label="Coste total del periodo"
                value={stageCostForm.monthly_total_amount}
                onChange={(event) =>
                  setStageCostForm((prev) => ({ ...prev, monthly_total_amount: event.target.value }))
                }
                fullWidth
                size="small"
              />
            ) : (
              <>
                <TextField
                  label="Coste hora"
                  value={stageCostForm.labor_hour_cost}
                  onChange={(event) =>
                    setStageCostForm((prev) => ({ ...prev, labor_hour_cost: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Horas del periodo"
                  value={stageCostForm.hours_total}
                  onChange={(event) =>
                    setStageCostForm((prev) => ({ ...prev, hours_total: event.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </>
            )}

            <TextField
              label="Notas"
              value={stageCostForm.notes}
              onChange={(event) => setStageCostForm((prev) => ({ ...prev, notes: event.target.value }))}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStageDialog}>Cancelar</Button>
          <Button onClick={() => void handleSubmitStageCost()} disabled={!stageDialogCanSubmit || savingStageCost}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={productImportDialogOpen} onClose={closeProductImportDialog} fullWidth maxWidth="md">
        <DialogTitle>Importar rendimientos por producto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />}>
                Seleccionar archivo
                <input
                  hidden
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    if (file) {
                      void handlePreviewProductImport(file);
                    }
                  }}
                />
              </Button>

              {productImportPreview && productImportFile ? (
                <Button
                  variant="outlined"
                  onClick={() =>
                    void handlePreviewProductImport(
                      productImportFile,
                      productImportMapping,
                      productImportProductResolution,
                      productImportStageResolution,
                    )
                  }
                  disabled={previewingProductImport}
                >
                  Actualizar vista previa
                </Button>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant={productImportStep === 'fields' ? 'contained' : 'outlined'}
                onClick={goToProductImportFieldsStep}
                disabled={!productImportPreview}
              >
                1. Campos
              </Button>
              <Button
                variant={productImportStep === 'values' ? 'contained' : 'outlined'}
                onClick={goToProductImportValuesStep}
                disabled={!productImportCanGoToValues}
              >
                2. Valores
              </Button>
              <Button
                variant={productImportStep === 'summary' ? 'contained' : 'outlined'}
                onClick={() => void handleGoToProductImportSummaryStep()}
                disabled={!productImportCanGoToSummary}
              >
                3. Resumen
              </Button>
            </Stack>

            {productImportFile ? (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Archivo seleccionado: {productImportFile.name}
              </Typography>
            ) : (
              <Alert severity="info">
                Sube un archivo CSV o XLSX con producto, etapa, base de producción y rendimiento por hora.
              </Alert>
            )}

            {!productImportPreview ? null : productImportStep === 'fields' ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  Primero confirma qué columnas del archivo corresponden a producto, etapa, base y rendimiento.
                </Alert>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.25,
                  }}
                >
                  {[
                    ['product', 'Producto'],
                    ['stage', 'Etapa'],
                    ['production_basis', 'Base de producción'],
                    ['units_per_hour', 'Rendimiento por hora'],
                    ['valuation_mode', 'Valoración'],
                    ['labor_hour_cost', 'Coste hora'],
                  ].map(([field, label]) => (
                    <TextField
                      key={field}
                      select
                      size="small"
                      label={label}
                      value={productImportMapping[field] ?? ''}
                      onChange={(event) =>
                        setProductImportMapping((prev) => ({
                          ...prev,
                          [field]: event.target.value || null,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="">Sin asignar</MenuItem>
                      {productImportPreview.columns.map((column) => (
                        <MenuItem key={`${field}-${column}`} value={column}>
                          {column}
                        </MenuItem>
                      ))}
                    </TextField>
                  ))}
                </Box>

                {productImportPreview.missing_required_fields.length > 0 ? (
                  <Alert severity="warning">
                    Faltan campos obligatorios en el mapeo: {productImportPreview.missing_required_fields.join(', ')}.
                  </Alert>
                ) : (
                  <Alert severity="success">
                    El mapeo de campos ya permite pasar al siguiente paso.
                  </Alert>
                )}

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapa</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Base</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Rendimiento</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Valoración</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productImportPreview.preview_rows.map((row, index) => (
                        <TableRow key={`labor-import-preview-fields-${index}`}>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.product ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.stage ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.production_basis ?? '—')}</TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>{String(row.units_per_hour ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.valuation_mode ?? '—')}</TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>{String(row.labor_hour_cost ?? '—')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            ) : productImportStep === 'values' ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  Resuelve aquí los valores de producto y etapa que no coincidan con los maestros actuales. Las filas que sigan sin resolver quedarán fuera de la importación.
                </Alert>

                {unresolvedProductRefs.length === 0 && unresolvedStageRefs.length === 0 ? (
                  <Alert severity="success">
                    No hay valores pendientes de resolver. Puedes pasar al resumen final.
                  </Alert>
                ) : null}

                {unresolvedProductRefs.length > 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.16)',
                      bgcolor: 'rgba(2,6,23,0.22)',
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Productos no reconocidos
                      </Typography>

                      {unresolvedProductRefs.map((item) => (
                        <Box
                          key={`product-resolution-${item.normalized_value}`}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            label="Valor detectado"
                            value={`${item.source_value} (${item.row_count} fila(s))`}
                            InputProps={{ readOnly: true }}
                            fullWidth
                          />
                          <TextField
                            select
                            size="small"
                            label="Resolver como producto"
                            value={productImportProductResolution[item.normalized_value] ?? ''}
                            onChange={(event) =>
                              setProductImportProductResolution((prev) => ({
                                ...prev,
                                [item.normalized_value]: event.target.value || null,
                              }))
                            }
                            fullWidth
                          >
                            <MenuItem value="">Omitir filas</MenuItem>
                            {products.map((product) => (
                              <MenuItem key={`product-resolution-option-${product.id}`} value={product.id}>
                                {[product.code, product.name].filter(Boolean).join(' · ')}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}

                {unresolvedStageRefs.length > 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.16)',
                      bgcolor: 'rgba(2,6,23,0.22)',
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Etapas no reconocidas
                      </Typography>

                      {unresolvedStageRefs.map((item) => (
                        <Box
                          key={`stage-resolution-${item.normalized_value}`}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            label="Valor detectado"
                            value={`${item.source_value} (${item.row_count} fila(s))`}
                            InputProps={{ readOnly: true }}
                            fullWidth
                          />
                          <TextField
                            select
                            size="small"
                            label="Resolver como etapa"
                            value={productImportStageResolution[item.normalized_value] ?? ''}
                            onChange={(event) =>
                              setProductImportStageResolution((prev) => ({
                                ...prev,
                                [item.normalized_value]: event.target.value || null,
                              }))
                            }
                            fullWidth
                          >
                            <MenuItem value="">Omitir filas</MenuItem>
                            {stages.map((stage) => (
                              <MenuItem key={`stage-resolution-option-${stage.id}`} value={stage.id}>
                                {[stage.code, stage.name].filter(Boolean).join(' · ')}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(2,6,23,0.28)',
                    px: 2,
                    py: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      Resumen de importación
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                      Total filas: {productImportPreview.coverage_summary.total_rows}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                      Importables completas: {productImportPreview.coverage_summary.rows_importable_complete} · Importables parciales: {productImportPreview.coverage_summary.rows_importable_partial} · No importables: {productImportPreview.coverage_summary.rows_not_importable}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Productos sin resolver: {productImportPreview.coverage_summary.unresolved_product_refs_count} · Etapas sin resolver: {productImportPreview.coverage_summary.unresolved_stage_refs_count}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Bloqueadas por producto: {productImportPreview.coverage_summary.rows_blocked_unresolved_product} · por etapa: {productImportPreview.coverage_summary.rows_blocked_unresolved_stage} · por base: {productImportPreview.coverage_summary.rows_blocked_invalid_basis} · por rendimiento: {productImportPreview.coverage_summary.rows_blocked_invalid_units_per_hour}
                    </Typography>
                  </Stack>
                </Paper>

                {productImportPreview.coverage_summary.rows_importable === 0 ? (
                  <Alert severity="error">
                    No hay ninguna fila importable con el archivo y el mapeo actual. La importación está bloqueada hasta resolver producto, etapa, base o rendimiento.
                  </Alert>
                ) : null}

                {productImportPreview.coverage_summary.rows_importable_partial > 0 ? (
                  <Alert severity="warning">
                    Hay {productImportPreview.coverage_summary.rows_importable_partial} fila(s) importable(s) pero parciales, sin coste hora. Se podrán guardar, pero no resolverán completamente el coste laboral cuando el modelo activo requiera coste hora.
                  </Alert>
                ) : null}

                {productImportPreview.coverage_summary.rows_not_importable > 0 ? (
                  <Alert severity="info">
                    Hay filas bloqueadas por producto, etapa, base o rendimiento inválido. Solo se importarán las filas válidas.
                  </Alert>
                ) : null}

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapa</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Base</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Rendimiento</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Valoración</TableCell>
                        <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 800 }}>Coste hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productImportPreview.preview_rows.map((row, index) => (
                        <TableRow key={`labor-import-preview-summary-${index}`}>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.product ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.stage ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.production_basis ?? '—')}</TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>{String(row.units_per_hour ?? '—')}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1' }}>{String(row.valuation_mode ?? '—')}</TableCell>
                          <TableCell align="right" sx={{ color: '#cbd5e1' }}>{String(row.labor_hour_cost ?? '—')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProductImportDialog}>Cancelar</Button>

          {productImportStep === 'fields' ? (
            <Button
              variant="contained"
              onClick={goToProductImportValuesStep}
              disabled={!productImportCanGoToValues || previewingProductImport}
            >
              Continuar
            </Button>
          ) : null}

          {productImportStep === 'values' ? (
            <>
              <Button variant="outlined" onClick={goToProductImportFieldsStep}>
                Volver
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleGoToProductImportSummaryStep()}
                disabled={!productImportCanGoToSummary || previewingProductImport}
              >
                Continuar
              </Button>
            </>
          ) : null}

          {productImportStep === 'summary' ? (
            <>
              <Button variant="outlined" onClick={goToProductImportValuesStep}>
                Volver
              </Button>
              <Button
                onClick={() => void handleConfirmProductImport()}
                disabled={!productImportCanConfirm || confirmingProductImport}
              >
                {productImportPreview?.coverage_summary.rows_importable === 0
                  ? 'Importación bloqueada'
                  : 'Confirmar importación'}
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={productDialogOpen} onClose={closeProductDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingProductRate ? 'Editar rendimiento por producto' : 'Nuevo rendimiento por producto'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Producto"
              value={productRateForm.product_id}
              onChange={(event) => setProductRateForm((prev) => ({ ...prev, product_id: event.target.value }))}
              fullWidth
              size="small"
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {[product.code, product.name].filter(Boolean).join(' · ')}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Etapa"
              value={productRateForm.stage_id}
              onChange={(event) => setProductRateForm((prev) => ({ ...prev, stage_id: event.target.value }))}
              fullWidth
              size="small"
            >
              {stages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {[stage.code, stage.name].filter(Boolean).join(' · ')}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Base de producción"
              value={productRateForm.production_basis}
              onChange={(event) =>
                setProductRateForm((prev) => ({
                  ...prev,
                  production_basis: event.target.value as LaborProductionBasis,
                }))
              }
              fullWidth
              size="small"
            >
              {BASIS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Rendimiento por hora"
              value={productRateForm.units_per_hour}
              onChange={(event) =>
                setProductRateForm((prev) => ({ ...prev, units_per_hour: event.target.value }))
              }
              fullWidth
              size="small"
            />

            <TextField
              select
              label="Valoración"
              value={productRateForm.valuation_mode}
              onChange={(event) =>
                setProductRateForm((prev) => ({
                  ...prev,
                  valuation_mode: event.target.value as LaborValuationMode,
                }))
              }
              fullWidth
              size="small"
            >
              {VALUATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Coste hora"
              value={productRateForm.labor_hour_cost}
              onChange={(event) =>
                setProductRateForm((prev) => ({ ...prev, labor_hour_cost: event.target.value }))
              }
              fullWidth
              size="small"
            />

            <TextField
              label="Notas"
              value={productRateForm.notes}
              onChange={(event) => setProductRateForm((prev) => ({ ...prev, notes: event.target.value }))}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProductDialog}>Cancelar</Button>
          <Button onClick={() => void handleSubmitProductRate()} disabled={!productDialogCanSubmit || savingProductRate}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}